import React, { useState } from 'react';
import { Plus, Pencil, Trash2, Save, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { useCreditReasons, useCreateCreditReason, useUpdateCreditReason, useDeleteCreditReason, CreditReason } from '@/hooks/useCreditReasons';

export const CreditSettings: React.FC = () => {
  const { toast } = useToast();
  const { data: creditReasons, isLoading } = useCreditReasons();
  const createCreditReason = useCreateCreditReason();
  const updateCreditReason = useUpdateCreditReason();
  const deleteCreditReason = useDeleteCreditReason();

  const [editingId, setEditingId] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editForm, setEditForm] = useState({
    reason_name: '',
    description: '',
    display_order: 0,
  });

  const handleEdit = (reason: CreditReason) => {
    setEditingId(reason.id);
    setEditForm({
      reason_name: reason.reason_name,
      description: reason.description || '',
      display_order: reason.display_order,
    });
    setShowAddForm(false);
  };

  const handleSave = async () => {
    if (editingId) {
      await updateCreditReason.mutateAsync({
        id: editingId,
        ...editForm,
      });
    } else {
      await createCreditReason.mutateAsync({
        ...editForm,
        is_active: true,
      });
    }
    
    setEditingId(null);
    setShowAddForm(false);
    setEditForm({ reason_name: '', description: '', display_order: 0 });
  };

  const handleCancel = () => {
    setEditingId(null);
    setShowAddForm(false);
    setEditForm({ reason_name: '', description: '', display_order: 0 });
  };

  const handleDelete = async (id: string) => {
    if (confirm('Are you sure you want to delete this credit reason?')) {
      await deleteCreditReason.mutateAsync(id);
    }
  };

  const handleAddNew = () => {
    setShowAddForm(true);
    setEditingId(null);
    const maxOrder = Math.max(...(creditReasons?.map(r => r.display_order) || [0]));
    setEditForm({ 
      reason_name: '', 
      description: '', 
      display_order: maxOrder + 1 
    });
  };

  if (isLoading) {
    return <div>Loading credit settings...</div>;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            Credit Reasons Management
            <Button onClick={handleAddNew} size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Add Reason
            </Button>
          </CardTitle>
          <CardDescription>
            Manage credit request reasons that users can select when creating credit requests.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {showAddForm && (
              <Card className="border-dashed">
                <CardContent className="pt-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="text-sm font-medium">Reason Name *</label>
                      <Input
                        value={editForm.reason_name}
                        onChange={(e) => setEditForm({...editForm, reason_name: e.target.value})}
                        placeholder="Enter reason name"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Display Order</label>
                      <Input
                        type="number"
                        value={editForm.display_order}
                        onChange={(e) => setEditForm({...editForm, display_order: parseInt(e.target.value) || 0})}
                      />
                    </div>
                    <div className="flex items-end gap-2">
                      <Button onClick={handleSave} size="sm" disabled={!editForm.reason_name.trim()}>
                        <Save className="h-4 w-4 mr-2" />
                        Save
                      </Button>
                      <Button onClick={handleCancel} variant="outline" size="sm">
                        <X className="h-4 w-4 mr-2" />
                        Cancel
                      </Button>
                    </div>
                  </div>
                  <div className="mt-4">
                    <label className="text-sm font-medium">Description</label>
                    <Textarea
                      value={editForm.description}
                      onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                      placeholder="Optional description"
                      rows={2}
                    />
                  </div>
                </CardContent>
              </Card>
            )}

            {creditReasons?.map((reason) => (
              <Card key={reason.id}>
                <CardContent className="pt-6">
                  {editingId === reason.id ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="text-sm font-medium">Reason Name *</label>
                          <Input
                            value={editForm.reason_name}
                            onChange={(e) => setEditForm({...editForm, reason_name: e.target.value})}
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium">Display Order</label>
                          <Input
                            type="number"
                            value={editForm.display_order}
                            onChange={(e) => setEditForm({...editForm, display_order: parseInt(e.target.value) || 0})}
                          />
                        </div>
                        <div className="flex items-end gap-2">
                          <Button onClick={handleSave} size="sm" disabled={!editForm.reason_name.trim()}>
                            <Save className="h-4 w-4 mr-2" />
                            Save
                          </Button>
                          <Button onClick={handleCancel} variant="outline" size="sm">
                            <X className="h-4 w-4 mr-2" />
                            Cancel
                          </Button>
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium">Description</label>
                        <Textarea
                          value={editForm.description}
                          onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                          rows={2}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-4">
                          <div className="bg-muted rounded px-2 py-1 text-xs font-mono">
                            #{reason.display_order}
                          </div>
                          <div>
                            <h4 className="font-medium">{reason.reason_name}</h4>
                            {reason.description && (
                              <p className="text-sm text-muted-foreground">{reason.description}</p>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleEdit(reason)}
                          variant="outline"
                          size="sm"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          onClick={() => handleDelete(reason.id)}
                          variant="outline"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}

            {!creditReasons?.length && !showAddForm && (
              <div className="text-center py-8 text-muted-foreground">
                No credit reasons configured. Click "Add Reason" to get started.
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};